import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import MonitorHeartOutlined from '@mui/icons-material/MonitorHeartOutlined';
import type { TFunction } from 'i18next';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeflex/primeflex.css';
import './RTUDataDisplay.css';
import '../common/ActionPresentation.css';
import { InstantSystemEvent, Module, RTUData } from '../../types/RtuData';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/store';
import { fetchRTUData } from '../../redux/slices/rtuSlice';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import RTUInfo from './RTUInfo';
import RtuDetailSummaryStrip from './RtuDetailSummaryStrip';
import SystemParameters from './SystemParameters';
import RTUDiagnosticsTabbedTree from './RTUDiagnosticsTabbedTree';
import SystemEvents from './SystemEvents';
import { resolveRtuDetailViewMode, RtuDetailViewMode } from '../../utils/rtuDetailViewMode';
import { toRtuActionTarget } from '../../utils/rtuActionTarget';
import RTUActions from '../RTUActions';
import actionRunsService from '../../services/actionRunsService';
import auditService from '../../services/auditService';
import { rtuStatusHistoryService } from '../../services/rtuStatusHistoryService';
import { fetchLatestRtuReachability, getRtuInstantSystemEventHistory } from '../../services/rtuService';
import { Action, ActionArtifact, ActionRunDetail, ActionTargetRun, LoginActivityDto } from '../../types/actionTypes';
import { RtuStatusHistoryResponse } from '../../types/RtuStatusHistory';
import { RtuPingRunResponseDto } from '../../types/RtuPing';
import { RtuSupportedRange } from '../../types/RtuStatus';
import RtuSessionCard from './RtuSessionCard';
import RtuCanonicalFreshnessCard from './RtuCanonicalFreshnessCard';
import RtuReachabilityActivityCard from './RtuReachabilityActivityCard';
import RtuFirewallCard from './RtuFirewallCard';
import { RtuServerPortsCard } from './RtuServerPortsCard';
import { RtuPortForwardCard } from './RtuPortForwardCard';
import RtuVirtualIpsCard from './RtuVirtualIpsCard';
import RtuConnectedDevicesCard from './RtuConnectedDevicesCard';
import { createModuleHealthStatusBodyTemplate } from './moduleHealthStatusTemplate';
import { useRtuDetailChrome } from '../../context/RtuDetailChromeContext';
import LegacyV2RtuDetailView from './LegacyV2RtuDetailView';
import { formatDisplayDateTime } from '../../utils/dateTimeFormat';
import ActionStatusChip from '../common/ActionStatusChip';
import ActionStatusIcon from '../common/ActionStatusIcon';
import { SkeletonBlock, SkeletonDetailPanel, SkeletonRegion, SkeletonRow, SkeletonTable } from '../common/Skeleton/Skeleton';
import { formatActionPhaseLabel } from '../../utils/actionProgress';
import { formatDuration } from '../../utils/formatDuration';
import { getActionStatusLabel } from '../../utils/actionStatusConfig';
import { getStatusColor, getStatusLabelKey, toUiStatus } from '../../utils/statusConfig';
import { formatRtuTimeZone } from '../../utils/rtuTimeZone';
import ActionArtifactList from '../common/ActionArtifactList';
import ActionLifecycleRow from '../common/ActionLifecycleRow';
import {
  actionInitiatorIp,
  actionTargetLabel,
  actionTypeDisplayName,
  describeDownloadSelection,
  formatActionDateTime,
} from '../../utils/actionPresentation';
import { actionToActionRunDetail } from '../../utils/actionRunMapper';
import ActionTransferProgressView from '../../features/actions/ActionTransferProgressView';
import { shouldShowRtuDetailError, shouldShowRtuDetailSkeleton } from '../../utils/rtuDetailPageGating';

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

export interface OverviewRow {
  label: string;
  value: React.ReactNode;
}

export interface OverviewSectionProps {
  title: string;
  rows: OverviewRow[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const summarizeDiagnosticValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (!isRecord(value)) {
    return String(value);
  }

  if (typeof value.status === 'string' && value.status.trim()) {
    return value.status;
  }

  const totalAsNumber =
    typeof value.total === 'number'
      ? value.total
      : typeof value.total === 'string' && value.total.trim() && !Number.isNaN(Number(value.total))
        ? Number(value.total)
        : null;

  const dataEntriesCount = isRecord(value.dataEntries) ? Object.keys(value.dataEntries).length : null;
  const entriesCount = isRecord(value.entries) ? Object.keys(value.entries).length : null;
  const fallbackCount = totalAsNumber ?? dataEntriesCount ?? entriesCount;

  if (fallbackCount !== null && fallbackCount !== undefined) {
    return `${fallbackCount} items`;
  }

  if (typeof value.config === 'string' && value.config.trim()) {
    return value.config;
  }

  return '—';
};

const summarizeStatusWithIp = (value: unknown) => {
  if (!isRecord(value)) {
    return summarizeDiagnosticValue(value);
  }

  const status = summarizeDiagnosticValue(value.status ?? value);
  const ip = toDisplayValue(value.ip);
  return ip === '—' ? status : `${status} (${ip})`;
};

export const renderOverviewValue = (value: React.ReactNode) => {
  if (React.isValidElement(value)) {
    return value;
  }

  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => renderOverviewValue(item))
      .filter((item) => typeof item === 'string' && item.trim().length > 0) as string[];

    return normalized.length > 0 ? normalized.join(', ') : '—';
  }

  return summarizeDiagnosticValue(value);
};

const toDisplayValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return summarizeDiagnosticValue(value);
};

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`rtu-detail-tabpanel-${index}`}
    aria-labelledby={`rtu-detail-tab-${index}`}
    className="rtu-detail-tabpanel"
  >
    {value === index ? children : null}
  </div>
);

const formatEventTimestamp = (event: InstantSystemEvent) =>
  formatDisplayDateTime(event.recordedAtUtc || event.updatedAtUtc || event.utcTime || event.utc || event.time || event.createdAt);

const getEventTimestamp = (event: InstantSystemEvent): number => {
  const raw = event.recordedAtUtc || event.updatedAtUtc || event.utcTime || event.utc || event.time || event.createdAt;
  if (raw === null || raw === undefined || raw === '') {
    return 0;
  }

  const numeric = Number(raw);
  if (Number.isFinite(numeric)) {
    return numeric > 0 && numeric < 10_000_000_000 ? numeric * 1000 : numeric;
  }

  const parsed = Date.parse(String(raw));
  return Number.isFinite(parsed) ? parsed : 0;
};

const ACTIVITY_RANGE_PRESETS: Array<{ range: RtuSupportedRange; label: string }> = [
  { range: '24h', label: '24H' },
  { range: '7d', label: '7D' },
  { range: '30d', label: '30D' },
];

const NOT_REPORTED_DEFAULT = 'Not reported';

const useRtuLatestLoginActivity = (rtuId: number | null | undefined) => {
  const [latestLogin, setLatestLogin] = useState<LoginActivityDto | null>(null);

  useEffect(() => {
    let active = true;
    setLatestLogin(null);

    if (!rtuId) {
      return () => {
        active = false;
      };
    }

    Promise.resolve(auditService.fetchLoginActivity({ rtuId, page: 0, size: 1 }))
      .then((page) => {
        if (active) {
          setLatestLogin(page?.content?.[0] ?? null);
        }
      })
      .catch(() => {
        if (active) {
          setLatestLogin(null);
        }
      });

    return () => {
      active = false;
    };
  }, [rtuId]);

  return latestLogin;
};

const extractRecordCandidate = <T,>(data: RTUData | null | undefined, keys: string[]): Record<string, T> => {
  const source = data as unknown as Record<string, unknown> | null | undefined;

  for (const key of keys) {
    const value = source?.[key];
    if (isRecord(value)) {
      return value as Record<string, T>;
    }
  }

  return {};
};

const getCurrentInstantEvents = (data: RTUData | null | undefined) => {
  const record = extractRecordCandidate<InstantSystemEvent>(data, [
    'currentInstantSystemEvents',
    'instantSystemEventsCurrent',
  ]);

  return Object.entries(record).map(([fallbackId, event]) => ({
    ...event,
    id: event?.id ?? event?.eventId ?? fallbackId,
  }));
};

export const OverviewSectionCard = ({ title, rows }: OverviewSectionProps) => (
  <Card title={title} className="info-card compact-card flat-card">
    <div className="scrollable-card-content">
      <table className="kv-table">
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              <td>{renderOverviewValue(row.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

const RtuOverviewCards = ({ data }: { data: RTUData | null | undefined }) => {
  const { t } = useTranslation();
  const header = data?.rtuHeader;
  const staticData = data?.rtuStaticData;

  const rows: OverviewRow[] = [
    { label: t('rtuDetail.infoRows.name', { defaultValue: 'RTU Name' }), value: staticData?.name },
    { label: t('dashboard.tables.columns.wellId', { defaultValue: 'Well ID' }), value: staticData?.wellID || data?.id },
    { label: t('rtuDetail.infoRows.serialNo', { defaultValue: 'Serial Number' }), value: header?.serialNo || data?.serialNo },
    { label: t('rtuDetail.infoRows.field', { defaultValue: 'Field' }), value: data?.field },
    { label: t('dashboard.tables.columns.producer', { defaultValue: 'Producer' }), value: data?.producer },
    { label: t('rtuDetail.infoRows.location', { defaultValue: 'Location' }), value: staticData?.location },
    { label: t('rtuDetail.infoRows.country', { defaultValue: 'Country' }), value: staticData?.country },
    {
      label: t('rtuDetail.infoRows.timeZone', { defaultValue: 'Time Zone' }),
      value: formatRtuTimeZone({
        headerTimeZone: header?.tz || data?.tz,
        deviceTimeZone: staticData?.timezone,
        timeZoneCountry: staticData?.tzCountry,
      }),
    },
  ];

  return (
    <OverviewSectionCard
      title={t('rtuDetail.sections.identitySiteSummary', { defaultValue: 'RTU and Site Overview' })}
      rows={rows}
    />
  );
};

const RtuCommunicationSummaryCard = ({ data }: { data: RTUData | null | undefined }) => {
  const { t } = useTranslation();
  const communication = data?.rtuDiagnostics?.communication;

  const rows: OverviewRow[] = [
    { label: 'I/O', value: summarizeDiagnosticValue(communication?.io?.status ?? communication?.io) },
    { label: 'DMS', value: summarizeDiagnosticValue(communication?.dms?.status ?? communication?.dms) },
    { label: 'Live Feed', value: summarizeDiagnosticValue(communication?.liveFeed?.status ?? communication?.liveFeed) },
    { label: 'Secondary RTU', value: summarizeDiagnosticValue(communication?.secondaryRtu?.status ?? communication?.secondaryRtu) },
    { label: 'Wireless HART', value: summarizeStatusWithIp(communication?.hartGateway) },
    { label: 'LoRa Gateway', value: summarizeStatusWithIp(communication?.loraGateway) },
    { label: 'VPN', value: summarizeDiagnosticValue(communication?.vpn?.status ?? communication?.vpn) },
    { label: 'Modem', value: summarizeDiagnosticValue(communication?.modem?.status ?? communication?.modem) },
    { label: 'Modbus Devices', value: summarizeDiagnosticValue(communication?.modbusDevices?.total ?? communication?.modbusDevices) },
  ];

  return <OverviewSectionCard title={t('rtuDetail.overviewGroups.communicationSummary')} rows={rows} />;
};

const RtuDataGenerationCard = ({ data }: { data: RTUData | null | undefined }) => {
  const diagnostics = data?.rtuDiagnostics?.dataFileGeneration;

  const rows: OverviewRow[] = [
    { label: 'Realtime Data', value: summarizeDiagnosticValue(diagnostics?.realtimeData) },
    { label: 'RBE Setpoint', value: summarizeDiagnosticValue(diagnostics?.rbeSetpoint) },
    { label: 'Smart Data', value: summarizeDiagnosticValue(diagnostics?.smartData) },
  ];

  return <OverviewSectionCard title="Data Generation" rows={rows} />;
};

const RtuTransferHealthCard = ({ data }: { data: RTUData | null | undefined }) => {
  const transfer = data?.rtuDiagnostics?.fileTransfer;
  const transferObjects = transfer?.fileTransferEntryObjects;
  const transferEntries = transfer?.fileTransferEntries;
  const addIpEntry = transferObjects?.['Add IP in File'];
  const timeSyncEntry = transferObjects?.['Time Synchronization'];

  const rows: OverviewRow[] = [
    { label: 'IP Detection', value: summarizeDiagnosticValue(transfer?.ipDetection ?? addIpEntry?.ip ?? addIpEntry?.status) },
    { label: 'Time Sync', value: summarizeDiagnosticValue(transfer?.timeSynchronization ?? timeSyncEntry?.status ?? timeSyncEntry) },
    { label: 'RTU ID Validation', value: summarizeDiagnosticValue(transfer?.rtuIdsValidation ?? transferEntries?.['RTU IDs Validation']) },
    { label: 'FTP', value: summarizeDiagnosticValue(transfer?.ftp) },
    { label: 'MQTT', value: summarizeDiagnosticValue(transfer?.mqtt) },
    { label: 'MQTT 2', value: summarizeDiagnosticValue(transfer?.mqtt2) },
    { label: 'MQTT 3', value: summarizeDiagnosticValue(transfer?.mqtt3) },
  ];

  return <OverviewSectionCard title="Transfer Health" rows={rows} />;
};

const RtuModemVpnCard = ({ data }: { data: RTUData | null | undefined }) => {
  const { t } = useTranslation();
  const modem = data?.rtuDiagnostics?.communication?.modem;
  const vpn = data?.rtuDiagnostics?.communication?.vpn;

  const rows: OverviewRow[] = [
    { label: t('rtuDetail.connectivity.vpnStatus'), value: vpn?.status || '—' },
    { label: t('rtuDetail.infoRows.vpnType'), value: data?.rtuHeader?.vpnType || '—' },
    { label: t('rtuDetail.connectivity.vpnConfiguration'), value: vpn?.config || '—' },
    { label: t('rtuDetail.connectivity.vpnIp'), value: vpn?.ip || '—' },
    { label: t('rtuDetail.connectivity.modemStatus'), value: modem?.status || '—' },
    { label: t('rtuDetail.connectivity.modemConfiguration'), value: modem?.config || '—' },
    { label: t('rtuDetail.connectivity.modemRuntimeIp'), value: modem?.ip || '—' },
    { label: t('rtuDetail.connectivity.modemSignal'), value: modem?.signalDb || '—' },
    { label: t('rtuDetail.connectivity.modemQuality'), value: modem?.quality || '—' },
  ];

  return <OverviewSectionCard title={t('rtuDetail.connectivity.modemAndVpn')} rows={rows} />;
};


const createModuleData = (rtuData: RTUData | null | undefined) => {
  if (!rtuData?.moduleStatus) {
    return [];
  }

  const allModules: any[] = [];

  const transformModules = (modules: Record<string, Module>) =>
    Object.entries(modules).map(([, moduleData]) => ({
      module: moduleData?.name,
      active: moduleData.active === 'Yes',
      license: moduleData.license === 'Yes',
      status: moduleData.status === 'Running',
    }));

  if (rtuData.moduleStatus.managerModules?.modules) {
    allModules.push(...transformModules(rtuData.moduleStatus.managerModules.modules));
  }
  if (rtuData.moduleStatus.scadaModules?.modules) {
    allModules.push(...transformModules(rtuData.moduleStatus.scadaModules.modules));
  }
  if (rtuData.moduleStatus.appModules?.modules) {
    allModules.push(...transformModules(rtuData.moduleStatus.appModules.modules));
  }

  return allModules;
};

const chunkArray = (items: any[], chunkSize: number) => {
  const results = [];
  const workingCopy = [...items];

  while (workingCopy.length) {
    results.push(workingCopy.splice(0, chunkSize));
  }

  return results;
};



const actionTargetTimestamp = (target: ActionTargetRun | null | undefined) =>
  target?.startedAt || target?.plannedAt || target?.completedAt;

const actionTargetPhaseLabel = (target: ActionTargetRun, t: TFunction) =>
  target.phaseLabel || (target.phaseCode ? formatActionPhaseLabel(target.phaseCode, t) : 'No phase reported');

const actionTargetTitle = (target: ActionTargetRun, t: TFunction) =>
  `${actionTypeDisplayName(target.actionType, t)} #${target.actionRunId}`;

const phaseState = (status: string | undefined): 'failed' | 'running' | 'plain' => {
  const normalized = String(status ?? '').toUpperCase();
  if (normalized === 'RUNNING' || normalized === 'IN_PROGRESS' || normalized === 'CANCELLING') {
    return 'running';
  }
  if (
    normalized === 'FAILED'
    || normalized === 'TIMED_OUT'
    || normalized === 'CANCELLED'
    || normalized === 'BLOCKED'
    || normalized === 'PARTIAL'
  ) {
    return 'failed';
  }
  return 'plain';
};

const phaseIcon = (status: string | undefined): string => {
  const state = phaseState(status);
  if (state === 'failed') {
    return 'pi pi-times-circle';
  }
  if (state === 'running') {
    return 'pi pi-spin pi-spinner';
  }
  return '';
};

const RtuActionPhaseText = ({ label, status }: { label: string; status: string | undefined }) => {
  const icon = phaseIcon(status);
  return (
    <span className={`rtu-actions-activity__phase-text rtu-actions-activity__phase-text--${phaseState(status)}`}>
      {icon ? <i className={icon} aria-hidden="true" /> : null}
      {label}
    </span>
  );
};

const actionTargetSortValue = (target: ActionTargetRun) => {
  const parsed = new Date(actionTargetTimestamp(target) || '').getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const mergeTargetRunSnapshot = (
  existing: ActionTargetRun | undefined,
  incoming: ActionTargetRun,
): ActionTargetRun => {
  if (!existing) return incoming;
  if (existing.version !== undefined && incoming.version !== undefined
      && incoming.version < existing.version) {
    return existing;
  }
  return {
    ...existing,
    ...incoming,
    transfer: incoming.transfer ?? existing.transfer,
    timeline: incoming.timeline ?? existing.timeline,
    commandHints: incoming.commandHints ?? existing.commandHints,
  };
};

const mergeTargetRunLists = (
  existing: ActionTargetRun[],
  incoming: ActionTargetRun[],
): ActionTargetRun[] => {
  const byId = new Map(existing.map((target) => [target.id, target]));
  incoming.forEach((target) => {
    byId.set(target.id, mergeTargetRunSnapshot(byId.get(target.id), target));
  });
  return Array.from(byId.values())
    .sort((left, right) => actionTargetSortValue(right) - actionTargetSortValue(left));
};

const actionSnapshotTargetsForRtu = (action: Action, rtuId: number): ActionTargetRun[] =>
  action.actionDetails
    .filter((detail) => detail.rtuId === rtuId && Number.isFinite(detail.id))
    .map((detail) => ({
      id: detail.id as number,
      actionRunId: action.id,
      userId: action.userId,
      username: action.username,
      actionType: action.actionType,
      runStatus: action.status,
      runPhaseCode: action.actionPhase,
      retrySourceActionId: action.retrySourceActionId,
      status: detail.status,
      phaseCode: detail.phase,
      phaseLabel: detail.phaseLabel,
      rtuId: detail.rtuId,
      rtuSerialNo: detail.rtuSerialNo,
      rtuName: detail.rtuName,
      ip: detail.ip,
      responseMessage: detail.responseMessage,
      plannedAt: action.createdAt,
      startedAt: detail.startedAt,
      completedAt: detail.completedAt,
      durationMs: detail.durationMs,
      commandHints: detail.commandHints,
      version: detail.revision,
      timeline: detail.timeline,
      transfer: detail.transfer,
    }));

const RtuActionsActivityCard = ({ rtuId }: { rtuId: number }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedActionRunId = Number(searchParams.get('actionRunId'));
  const panelContentRef = useRef<HTMLDivElement | null>(null);
  const [targetRuns, setTargetRuns] = useState<ActionTargetRun[]>([]);
  const [totalTargets, setTotalTargets] = useState(0);
  const [selectedTarget, setSelectedTarget] = useState<ActionTargetRun | null>(null);
  const [selectedAction, setSelectedAction] = useState<ActionRunDetail | null>(null);
  const [downloadingArtifactId, setDownloadingArtifactId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [commandBusy, setCommandBusy] = useState<'cancel' | 'retry' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);
  const allActionSnapshots = useSelector((state: RootState) => state.actions.actions);
  const actionSnapshots = useMemo(
    () => allActionSnapshots.filter((action) => action.actionDetails.some((detail) => detail.rtuId === rtuId)),
    [allActionSnapshots, rtuId],
  );

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await actionRunsService.fetchRtuTargetRuns(rtuId, { page: 0, size: 25 });
        if (active) {
          const sorted = [...page.content].sort((left, right) => actionTargetSortValue(right) - actionTargetSortValue(left));
          setTargetRuns((current) => mergeTargetRunLists(current, sorted));
          setTotalTargets((current) => Math.max(current, page.totalElements ?? sorted.length));
          setSelectedTarget((current) => {
            if (current) {
              const incoming = sorted.find((target) => target.id === current.id);
              return incoming ? mergeTargetRunSnapshot(current, incoming) : current;
            }
            if (Number.isInteger(requestedActionRunId) && requestedActionRunId > 0) {
              return sorted.find((target) => target.actionRunId === requestedActionRunId) ?? sorted[0] ?? null;
            }
            return sorted[0] ?? null;
          });
        }
      } catch (loadError: any) {
        if (active) {
          setTargetRuns([]);
          setTotalTargets(0);
          setSelectedTarget(null);
          setError(
            loadError?.response?.data?.message ||
              loadError?.message ||
              t('actionsWorkbench.loadError', { defaultValue: 'Failed to load actions.' })
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void run();
    return () => {
      active = false;
    };
  }, [requestedActionRunId, rtuId, t]);

  useEffect(() => {
    if (!actionSnapshots.length) return;
    const snapshotTargets = actionSnapshots.flatMap((action) => actionSnapshotTargetsForRtu(action, rtuId));
    if (!snapshotTargets.length) return;

    setTargetRuns((current) => {
      return mergeTargetRunLists(current, snapshotTargets);
    });

    setSelectedTarget((current) => {
      if (current) {
        const incoming = snapshotTargets.find((target) => target.id === current.id);
        return incoming ? mergeTargetRunSnapshot(current, incoming) : current;
      }
      if (requestedActionRunId > 0) {
        return snapshotTargets.find((target) => target.actionRunId === requestedActionRunId) ?? null;
      }
      return snapshotTargets[0] ?? null;
    });

  }, [actionSnapshots, requestedActionRunId, rtuId, selectedTarget?.actionRunId]);

  useEffect(() => {
    setTotalTargets((current) => Math.max(current, targetRuns.length));
  }, [targetRuns.length]);

  const selectedActionSnapshot = useMemo(() => {
    const selectedActionId = selectedTarget?.actionRunId || (requestedActionRunId > 0 ? requestedActionRunId : null);
    return selectedActionId
      ? actionSnapshots.find((action) => action.id === selectedActionId)
      : undefined;
  }, [actionSnapshots, requestedActionRunId, selectedTarget?.actionRunId]);
  const visibleSelectedAction = useMemo(
    () => selectedActionSnapshot ? actionToActionRunDetail(selectedActionSnapshot) : selectedAction,
    [selectedAction, selectedActionSnapshot],
  );
  const selectedTargetArtifact = useMemo(
    () => (visibleSelectedAction?.artifacts ?? []).find((artifact) => artifact.targetRunId === selectedTarget?.id),
    [visibleSelectedAction, selectedTarget?.id],
  );

  const latestTarget = targetRuns[0];
  const latestStatus = latestTarget?.status
    ? getActionStatusLabel(latestTarget.status, t)
    : t('rtuDetail.missing.notReported', { defaultValue: NOT_REPORTED_DEFAULT });
  const latestCreated = actionTargetTimestamp(latestTarget)
    ? formatDisplayDateTime(actionTargetTimestamp(latestTarget))
    : t('rtuDetail.missing.notReported', { defaultValue: NOT_REPORTED_DEFAULT });

  const selectTarget = (target: ActionTargetRun) => {
    setSelectedTarget(target);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'actions');
    nextParams.set('actionRunId', String(target.actionRunId));
    setSearchParams(nextParams, { replace: true });
  };

  const upsertTargetRun = (updatedTarget: ActionTargetRun) => {
    setTargetRuns((current) => {
      const exists = current.some((target) => target.id === updatedTarget.id);
      const next = exists
        ? current.map((target) => (target.id === updatedTarget.id ? updatedTarget : target))
        : [updatedTarget, ...current];
      return next.sort((left, right) => actionTargetSortValue(right) - actionTargetSortValue(left));
    });
    setSelectedTarget(updatedTarget);
  };

  const runTargetCommand = async (command: 'cancel' | 'retry') => {
    if (!selectedTarget) {
      return;
    }

    setCommandBusy(command);
    setError(null);
    try {
      let updatedTarget: ActionTargetRun;
      if (command === 'cancel') {
        await actionRunsService.cancelActionTargetRun(selectedTarget.id);
        updatedTarget = await actionRunsService.fetchActionTargetRun(selectedTarget.id);
      } else {
        updatedTarget = await actionRunsService.retryActionTargetRun(selectedTarget.id);
      }
      upsertTargetRun(updatedTarget);
    } catch (commandError: any) {
      setError(
        commandError?.response?.data?.message ||
          commandError?.message ||
          t('actionsWorkbench.commandFailed', { defaultValue: 'Action command failed.' })
      );
    } finally {
      setCommandBusy(null);
    }
  };

  useEffect(() => {
    let active = true;
    if (!selectedTarget?.actionRunId) {
      setSelectedAction(null);
      return () => {
        active = false;
      };
    }

    actionRunsService.fetchActionRun(selectedTarget.actionRunId)
      .then((run) => {
        if (active) {
          setSelectedAction(run);
        }
      })
      .catch(() => {
        if (active) {
          setSelectedAction(null);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedTarget?.actionRunId]);

  const downloadArtifact = async (artifact: ActionArtifact) => {
    if (!selectedTarget) return;
    setDownloadingArtifactId(artifact.id);
    setError(null);
    try {
      await actionRunsService.downloadArtifact(selectedTarget.actionRunId, artifact);
    } catch (downloadError: any) {
      setError(downloadError?.response?.data?.message || downloadError?.message || 'The action file could not be downloaded.');
    } finally {
      setDownloadingArtifactId(null);
    }
  };

  useLayoutEffect(() => {
    const content = panelContentRef.current;
    if (!content) {
      setPanelHeight(null);
      return undefined;
    }

    const updatePanelHeight = () => {
      setPanelHeight(Math.ceil(content.scrollHeight + 24));
    };

    updatePanelHeight();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(updatePanelHeight);
    observer.observe(content);
    return () => observer.disconnect();
  }, [loading, selectedTarget, targetRuns.length, error]);

  return (
    <Card className="info-card compact-card flat-card rtu-actions-activity-card">
      {error ? <div className="rtu-activity-card__state rtu-activity-card__state--error">{error}</div> : null}
      <div className="rtu-actions-activity__summary">
        <span>
          <i className="pi pi-sitemap" aria-hidden="true" />
          {loading ? <SkeletonBlock width="1.4rem" height="0.8rem" style={{ display: 'inline-block' }} /> : totalTargets} {t('rtuDetail.actionsActivity.totalShort', { defaultValue: 'target actions' })}
        </span>
        <span>
          <i className="pi pi-info-circle" aria-hidden="true" />
          {t('rtuDetail.actionsActivity.latestStatus', { defaultValue: 'Latest status' })}: {loading ? <SkeletonBlock width="4rem" height="0.8rem" style={{ display: 'inline-block' }} /> : latestStatus}
        </span>
        <span>
          <i className="pi pi-calendar" aria-hidden="true" />
          {loading ? <SkeletonBlock width="6rem" height="0.8rem" style={{ display: 'inline-block' }} /> : latestCreated}
        </span>
      </div>
      <div
        className="rtu-actions-activity"
        style={panelHeight ? ({ '--rtu-actions-activity-height': `${panelHeight}px` } as React.CSSProperties) : undefined}
      >
        <div className="rtu-actions-activity__list" role="list" aria-label={t('rtuDetail.sections.actionsActivity', { defaultValue: 'RTU Actions Activity' })}>
          {loading && targetRuns.length === 0 ? (
            <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
              <SkeletonRow count={4} />
            </SkeletonRegion>
          ) : null}
          {targetRuns.length === 0 && !loading ? (
            <div className="rtu-activity-card__state">
              {t('rtuDetail.actionsActivity.empty', { defaultValue: 'No action target runs found for this RTU.' })}
            </div>
          ) : null}
          {targetRuns.map((target) => (
            <button
              type="button"
              key={target.id}
              className={`rtu-actions-activity__row${selectedTarget?.id === target.id ? ' rtu-actions-activity__row--selected' : ''}`}
              onClick={() => selectTarget(target)}
            >
              <ActionStatusIcon status={target.status} />
              <span className="rtu-actions-activity__row-main">
                <strong>{actionTargetTitle(target, t)}</strong>
                <span className="rtu-actions-activity__row-meta">
                  <span>{target.username || target.userId || '—'}</span>
                  <RtuActionPhaseText label={actionTargetPhaseLabel(target, t)} status={target.status} />
                </span>
                {target.responseMessage ? <small>{target.responseMessage}</small> : null}
              </span>
              <span className="rtu-actions-activity__row-time">
                <strong>{formatDisplayDateTime(actionTargetTimestamp(target))}</strong>
                <small>{formatDuration(target.durationMs)}</small>
              </span>
            </button>
          ))}
        </div>
        <aside className="rtu-actions-activity__panel">
          <div className="rtu-actions-activity__panel-content" ref={panelContentRef}>
            {selectedTarget ? (
              <>
              <div className="rtu-actions-activity__panel-header">
                <div>
                  <h3>{actionTargetTitle(selectedTarget, t)}</h3>
                  <button
                    type="button"
                    className="rtu-actions-activity__copy-id"
                    title="Copy action ID"
                    onClick={() => void navigator.clipboard?.writeText(String(selectedTarget.actionRunId))}
                  >
                    <i className="pi pi-copy" aria-hidden="true" /> Action ID {selectedTarget.actionRunId}
                  </button>
                </div>
                <ActionStatusChip status={selectedTarget.status} />
              </div>
              <div className="rtu-actions-activity__lifecycle-row">
                <RtuActionPhaseText label={actionTargetPhaseLabel(selectedTarget, t)} status={selectedTarget.status} />
                <div className="rtu-actions-activity__panel-actions">
                  <Button
                    className="rtu-actions-activity__icon-button"
                    size="small"
                    text
                    rounded
                    icon="pi pi-times"
                    label={t('actionsWorkbench.cancelTarget', { defaultValue: 'Cancel target' })}
                    severity="danger"
                    loading={commandBusy === 'cancel'}
                    disabled={!selectedTarget.commandHints?.cancellable || commandBusy !== null}
                    tooltip={selectedTarget.commandHints?.reason || selectedTarget.commandHints?.cancelUnavailableReason || t('actionsWorkbench.cancel', { defaultValue: 'Cancel' })}
                    onClick={() => void runTargetCommand('cancel')}
                  />
                  <Button
                    className="rtu-actions-activity__icon-button"
                    size="small"
                    text
                    rounded
                    icon="pi pi-refresh"
                    label={t('actionsWorkbench.retry', { defaultValue: 'Retry' })}
                    loading={commandBusy === 'retry'}
                    disabled={!selectedTarget.commandHints?.retryable || commandBusy !== null}
                    tooltip={selectedTarget.commandHints?.retryUnavailableReason || t('actionsWorkbench.retry', { defaultValue: 'Retry' })}
                    onClick={() => void runTargetCommand('retry')}
                  />
                </div>
              </div>
              <ActionLifecycleRow
                createdAt={visibleSelectedAction?.createdAt || selectedTarget.plannedAt}
                startedAt={selectedTarget.startedAt}
                completedAt={selectedTarget.completedAt}
              />
              <dl className="rtu-actions-activity__details rtu-actions-activity__details--secondary">
                <div><dt><i className="pi pi-stopwatch" /> {t('actionsWorkbench.duration', { defaultValue: 'Duration' })}</dt><dd>{formatDuration(selectedTarget.durationMs)}</dd></div>
                <div><dt><i className="pi pi-user" /> {t('actionsWorkbench.launchedBy', { defaultValue: 'Launched by' })}</dt><dd>{selectedTarget.username || selectedTarget.userId || '—'}</dd></div>
                <div><dt><i className="pi pi-map-marker" /> {t('actionsWorkbench.userIp', { defaultValue: 'User IP' })}</dt><dd>{actionInitiatorIp(visibleSelectedAction) || 'Not reported'}</dd></div>
                <div><dt><i className="pi pi-sitemap" /> {t('rtuDetail.actionsActivity.target', { defaultValue: 'Target' })}</dt><dd>{actionTargetLabel(selectedTarget)}</dd></div>
                <div><dt><i className="pi pi-globe" /> {t('rtuDetail.infoRows.rtuPublicIp', { defaultValue: 'RTU public IP' })}</dt><dd>{selectedTarget.ip || '—'}</dd></div>
                {describeDownloadSelection(visibleSelectedAction?.actionType, visibleSelectedAction?.downloadSelection, t) ? (
                  <div><dt><i className="pi pi-filter" /> {t('actionsWorkbench.selection', { defaultValue: 'Selection' })}</dt><dd>{describeDownloadSelection(visibleSelectedAction?.actionType, visibleSelectedAction?.downloadSelection, t)}</dd></div>
                ) : null}
                {selectedTarget.retrySourceActionId ? (
                  <div><dt><i className="pi pi-replay" /> {t('actionsWorkbench.retryOf', { defaultValue: 'Retry of' })}</dt><dd>#{selectedTarget.retrySourceActionId}</dd></div>
                ) : null}
              </dl>
              <ActionTransferProgressView
                transfer={selectedTarget.transfer}
                onDownload={selectedTargetArtifact ? () => downloadArtifact(selectedTargetArtifact) : undefined}
                downloadLabel={selectedTargetArtifact ? t('actionsModal.artifacts.download', { defaultValue: 'Download {{fileName}}', fileName: selectedTargetArtifact.fileName }) : undefined}
                downloading={selectedTargetArtifact ? downloadingArtifactId === selectedTargetArtifact.id : false}
                fileName={selectedTargetArtifact?.fileName}
              />
              {selectedTarget.responseMessage ? (
                <div className="rtu-actions-activity__response">
                  <span>{t('rtuDetail.actionsActivity.response', { defaultValue: 'Response / error message' })}</span>
                  <strong>{selectedTarget.responseMessage}</strong>
                </div>
              ) : null}
              {(selectedTarget.timeline ?? []).length > 0 ? (
                <div className="rtu-actions-activity__timeline">
                  <h4>Execution timeline</h4>
                  {selectedTarget.timeline?.map((event, index) => (
                    <div className="rtu-actions-activity__timeline-event" key={event.id ?? index}>
                      <span className="rtu-actions-activity__timeline-heading">
                        <time>{formatActionDateTime(event.occurredAtUtc || event.occurredAt, 'Time not reported')}</time>
                        <strong>{event.phaseLabel || event.phaseCode || event.status || 'Update'}</strong>
                      </span>
                      {event.message ? <span>{event.message}</span> : null}
                    </div>
                  ))}
                </div>
              ) : null}
              <ActionArtifactList
                artifacts={(visibleSelectedAction?.artifacts ?? []).filter((artifact) => !artifact.targetRunId || artifact.targetRunId === selectedTarget.id)}
                downloadingId={downloadingArtifactId}
                onDownload={downloadArtifact}
                targetNameById={{ [selectedTarget.id]: actionTargetLabel(selectedTarget) }}
              />
              <Link className="rtu-detail-link-button" to={`/actions?actionRunId=${selectedTarget.actionRunId}`}>
                {t('actionsModal.progress.openInAllActions', { defaultValue: 'Open in All Actions' })}
              </Link>
              </>
            ) : loading ? (
              <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
                <SkeletonDetailPanel />
              </SkeletonRegion>
            ) : (
              <div className="rtu-activity-card__state">
                {t('actionsWorkbench.selectPrompt', { defaultValue: 'Select an action to inspect targets, outcome, retry lineage, and management actions.' })}
              </div>
            )}
          </div>
        </aside>
      </div>
    </Card>
  );
};

const RtuLoginHistoryCard = ({ rtuId }: { rtuId: number }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LoginActivityDto[]>([]);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await auditService.fetchLoginActivity({
          rtuId,
          page: Math.floor(first / pageSize),
          size: pageSize,
          sort: 'loginUtcTime,desc',
        });
        if (active) {
          setRows(page.content);
          setTotalRecords(page.totalElements);
        }
      } catch (loadError: any) {
        if (active) {
          setRows([]);
          setTotalRecords(0);
          setError(
            loadError?.response?.data?.message ||
              loadError?.message ||
              t('audit.report.errors.loadFailed')
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void run();
    return () => {
      active = false;
    };
  }, [first, pageSize, rtuId, t]);

  useEffect(() => {
    setFirst(0);
  }, [rtuId]);

  return (
    <Card title={t('rtuDetail.sections.loginHistory', { defaultValue: 'Login History' })} className="info-card compact-card flat-card">
      {error ? <div className="rtu-activity-card__state rtu-activity-card__state--error">{error}</div> : null}
      {loading && rows.length === 0 ? (
        <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
          <SkeletonTable columns={[1.2, 1.2, 1, 1.3]} rows={5} showHeader={false} />
        </SkeletonRegion>
      ) : (
        <DataTable
          value={rows}
          loading={loading}
          className="p-datatable-sm"
          showGridlines
          emptyMessage={t('audit.report.empty.loginActivity')}
          tableStyle={{ width: '100%', minWidth: 'max-content' }}
          paginator
          lazy
          first={first}
          rows={pageSize}
          totalRecords={totalRecords}
          rowsPerPageOptions={[10, 25, 50]}
          onPage={(event) => {
            setFirst(event.first);
            setPageSize(event.rows);
          }}
        >
          <Column field="username" header={t('audit.report.columns.username')} style={{ width: '10rem' }} />
          <Column field="sourceIp" header={t('audit.report.columns.sourceIp')} style={{ width: '10rem' }} />
          <Column field="loginType" header={t('audit.report.columns.loginType')} style={{ width: '8rem' }} />
          <Column
            field="loginUtcTime"
            header={t('audit.report.columns.loginUtcTime')}
            style={{ width: '11rem' }}
            body={(row: LoginActivityDto) => formatDisplayDateTime(row.loginUtcTime)}
          />
        </DataTable>
      )}
    </Card>
  );
};

const RtuStatusHistoryActivityCard = ({ rtuId }: { rtuId: number }) => {
  const { t } = useTranslation();
  const [range, setRange] = useState<RtuSupportedRange>('24h');
  const [data, setData] = useState<RtuStatusHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await rtuStatusHistoryService.fetch({ range, rtuId });
        if (active) {
          setData(response);
        }
      } catch (loadError: any) {
        if (active) {
          setData(null);
          setError(
            loadError?.response?.data?.message ||
              loadError?.message ||
              t('dashboard.history.errors.loadFailed', {
                defaultValue: 'Unable to load fleet status history.',
              })
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void run();
    return () => {
      active = false;
    };
    // `t` deliberately excluded - it is only used for a failure-message
    // string, and including it here re-fires this fetch on every unrelated
    // Redux/i18next re-render (see src/locales/i18n.ts).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, rtuId]);

  const summary = data?.summary;
  const pointsCount = data?.points?.length ?? 0;
  const offlineOrNotReachableCount = (summary?.latestOfflineCount ?? 0) + (summary?.latestUnknownCount ?? 0);

  return (
    <Card title={t('dashboard.page.cards.statusHistory')} className="info-card compact-card flat-card">
      <div className="dashboard-history-chart__chip-group" style={{ marginBottom: '0.5rem' }}>
        {ACTIVITY_RANGE_PRESETS.map((preset) => (
          <button
            key={preset.range}
            type="button"
            className={`dashboard-history-chart__chip${range === preset.range ? ' is-active' : ''}`}
            onClick={() => setRange(preset.range)}
          >
            {preset.label}
          </button>
        ))}
      </div>
      {error ? <div className="rtu-activity-card__state rtu-activity-card__state--error">{error}</div> : null}
      <div className="rtu-activity-summary-grid">
        <div className="rtu-activity-summary-tile">
          <span>{t('status.online')}</span>
          <strong>
            {loading && !summary ? (
              <SkeletonBlock width="2rem" height="0.9rem" style={{ display: 'inline-block' }} />
            ) : (
              summary?.latestOnlineCount ?? 0
            )}
          </strong>
        </div>
        <div className="rtu-activity-summary-tile">
          <span>{t('status.offlineNotReachable', { defaultValue: 'Offline / Not Reachable' })}</span>
          <strong>
            {loading && !summary ? (
              <SkeletonBlock width="2rem" height="0.9rem" style={{ display: 'inline-block' }} />
            ) : (
              offlineOrNotReachableCount
            )}
          </strong>
        </div>
        <div className="rtu-activity-summary-tile">
          <span>{t('status.unstable', { defaultValue: 'Unstable' })}</span>
          <strong>
            {loading && !summary ? (
              <SkeletonBlock width="2rem" height="0.9rem" style={{ display: 'inline-block' }} />
            ) : (
              summary?.latestUnstableCount ?? 0
            )}
          </strong>
        </div>
      </div>
      <div className="rtu-activity-card__state">
        {loading
          ? <SkeletonBlock width="12rem" height="0.85rem" style={{ display: 'inline-block' }} />
          : t('dashboard.history.summary.bucketSummary', {
              defaultValue: '{{count}} buckets loaded; {{transitions}} transitions.',
              count: pointsCount,
              transitions: summary?.transitionCount ?? 0,
            })}
      </div>
    </Card>
  );
};

export const RtuLatestPingCard = ({ rtuId }: { rtuId: number }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<RtuPingRunResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchLatestRtuReachability(rtuId);
        if (active) {
          setData(response);
        }
      } catch (loadError: any) {
        if (active) {
          setError(loadError?.message || t('dashboard.reachability.errors.loadFailed', { defaultValue: 'Unable to load reachability.' }));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void run();
    return () => {
      active = false;
    };
  }, [rtuId, t]);

  const rawUiStatus = toUiStatus(data?.resultStatus);
  const uiStatus = rawUiStatus === 'unknown' ? 'offline' : rawUiStatus;
  const statusLabel = data
    ? uiStatus === 'offline'
      ? t('status.offlineNotReachable', { defaultValue: 'Offline / Not Reachable' })
      : t(getStatusLabelKey(uiStatus), { defaultValue: uiStatus.charAt(0).toUpperCase() + uiStatus.slice(1) })
    : '-';
  const successText = data ? `${data.successCount ?? 0}/${data.attemptCount ?? 0}` : '-';
  const attemptMarkers = data?.attempts?.length
    ? data.attempts.map((attempt) => {
        const normalized = String(attempt.result ?? '').toUpperCase();
        return {
          key: attempt.attemptNumber,
          success: normalized === 'SUCCESS',
          title: `#${attempt.attemptNumber} ${attempt.reason || attempt.result || ''}`.trim(),
        };
      })
    : [];

  const targetLabel = t('rtuActions.dialogs.pingRun.target', { defaultValue: 'Target' });
  const successLabel = t('rtuActions.dialogs.pingRun.success', { defaultValue: 'Success' });
  const lossLabel = t('rtuActions.dialogs.pingRun.loss', { defaultValue: 'Loss' });
  const avgLabel = t('rtuActions.dialogs.pingRun.avg', { defaultValue: 'Avg Time' });
  const lossText = data ? `${Number(data.packetLossPercent ?? 0).toFixed(1)}%` : '—';
  const avgText = data?.avgResponseMs == null ? '—' : `${Number(data.avgResponseMs).toFixed(0)} ms`;

  return (
    <Card title={t('rtuDetail.status.latestPing', { defaultValue: 'Latest Ping Result' })} className="info-card compact-card flat-card rtu-latest-ping-card-shell">
      {error ? <div className="rtu-activity-card__state rtu-activity-card__state--error">{error}</div> : null}
      <div className="scrollable-card-content">
        <table className="kv-table">
          <tbody>
            <tr>
              <td>{t('table.status', { defaultValue: 'Status' })}</td>
              <td>
                {loading ? (
                  <SkeletonBlock width="5rem" height="0.9rem" style={{ display: 'inline-block' }} />
                ) : (
                  <span className="rtu-latest-ping-card__status-value" style={{ color: getStatusColor(uiStatus) }}>
                    {statusLabel}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>{targetLabel}</td>
              <td className="rtu-latest-ping-card__target-value">{data?.targetIp || '—'}</td>
            </tr>
          </tbody>
        </table>
        <div className="rtu-latest-ping-card__metrics">
          <div>
            <span>{successLabel}</span>
            <strong>{successText}</strong>
          </div>
          <div>
            <span>{lossLabel}</span>
            <strong>{lossText}</strong>
          </div>
          <div>
            <span>{avgLabel}</span>
            <strong>{avgText}</strong>
          </div>
        </div>
        {attemptMarkers.length ? (
          <div className="rtu-latest-ping-card__attempts-row">
            <span className="rtu-latest-ping-card__attempts-label">
              {t('rtuActions.dialogs.pingRun.attempts', { defaultValue: 'Attempts' })}
            </span>
            <div className="rtu-latest-ping-card__attempts" role="list" aria-label={t('rtuActions.dialogs.pingRun.attempts', { defaultValue: 'Ping attempts' })}>
              {attemptMarkers.map((attempt) => (
                <span
                  key={attempt.key}
                  role="listitem"
                  title={attempt.title}
                  aria-label={attempt.title}
                  className={`rtu-latest-ping-card__attempt rtu-latest-ping-card__attempt--${attempt.success ? 'success' : 'failure'}`}
                >
                  {attempt.success ? '✓' : '×'}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      <div className="rtu-latest-ping-card__timestamp">
        {data?.completedAtUtc ? (
          <>
            <span>{t('rtuDetail.status.pingCompletedAt', { defaultValue: 'Completed' })}</span>
            {formatDisplayDateTime(data.completedAtUtc)}
          </>
        ) : (
          t('rtuDetail.status.noPingYet', { defaultValue: 'No ping run is available yet.' })
        )}
      </div>
    </Card>
  );
};

const RtuConnectivityCard = ({ data }: { data: RTUData | null | undefined }) => {
  const { t } = useTranslation();
  const { rtuHeader, rtuDiagnostics, rtuStaticData } = data || {};
  const modem = rtuDiagnostics?.communication?.modem;
  const vpn = rtuDiagnostics?.communication?.vpn;

  const rows = [
    { label: t('rtuDetail.connectivity.publicIp'), value: rtuHeader?.publicIp },
    { label: t('rtuDetail.connectivity.ethernetIp'), value: rtuHeader?.ethernetIP },
    { label: t('rtuDetail.connectivity.modemIp'), value: rtuHeader?.modemIP },
    {
      label: t('rtuDetail.connectivity.timeZone'),
      value: formatRtuTimeZone({
        headerTimeZone: rtuHeader?.tz || data?.tz,
        deviceTimeZone: rtuStaticData?.timezone,
        timeZoneCountry: rtuStaticData?.tzCountry,
      }),
    },
    { label: t('rtuDetail.connectivity.lastReport'), value: formatDisplayDateTime(rtuHeader?.utcTime) },
    { label: t('rtuDetail.connectivity.vpnStatus'), value: vpn?.status || '—' },
    { label: t('rtuDetail.connectivity.vpnIp'), value: vpn?.ip || '—' },
    { label: t('rtuDetail.connectivity.modemStatus'), value: modem?.status || '—' },
    { label: t('rtuDetail.connectivity.modemSignal'), value: modem?.signalDb || '—' },
    { label: t('rtuDetail.connectivity.modemQuality'), value: modem?.quality || '—' },
  ];

  return (
    <Card title={t('rtuDetail.sections.connectivity')} className="info-card compact-card flat-card">
      <div className="scrollable-card-content">
        <table className="kv-table">
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{row.value || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const RtuEventSummaryCard = ({ data }: { data: RTUData | null | undefined }) => {
  const { t } = useTranslation();
  const events = Object.values(data?.systemEvents || {});
  const currentInstantEvents = getCurrentInstantEvents(data);
  const total = events.length;
  const alarmCount = events.filter((event) => event?.status === 'Alarm').length;
  const noAlarmCount = events.filter((event) => event?.status === 'No Alarm').length;
  const latestTimeRaw = events.reduce<number | null>((latest, event) => {
    const parsed = Number(event?.time);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return latest;
    }

    if (latest === null || parsed > latest) {
      return parsed;
    }

    return latest;
  }, null);

  const rows: OverviewRow[] = [
    { label: t('rtuDetail.eventsSummary.totalEvents'), value: String(total) },
    { label: t('rtuDetail.eventsSummary.instantCurrentEvents', { defaultValue: 'Current Instant Event Statuses' }), value: String(currentInstantEvents.length) },
    { label: t('rtuDetail.eventsSummary.alarmEvents'), value: String(alarmCount) },
    { label: t('rtuDetail.eventsSummary.noAlarmEvents'), value: String(noAlarmCount) },
    {
      label: t('rtuDetail.eventsSummary.latestRecorded'),
      value: latestTimeRaw ? formatDisplayDateTime(latestTimeRaw * 1000) : '—',
    },
  ];

  return <OverviewSectionCard title={t('rtuDetail.sections.eventsSummary')} rows={rows} />;
};

const RtuInstantEventsTable = ({
  title,
  rows,
}: {
  title: string;
  rows: InstantSystemEvent[];
}) => {
  const { t } = useTranslation();

  return (
    <Card title={title} className="info-card compact-card flat-card">
      {rows.length === 0 ? (
        <div className="rtu-detail-empty-state">
          {t('rtuDetail.missing.notReported', { defaultValue: NOT_REPORTED_DEFAULT })}
        </div>
      ) : (
        <DataTable
          value={rows}
          className="p-datatable-sm"
          showGridlines
          size="small"
          tableStyle={{ width: '100%', minWidth: 'max-content' }}
        >
          <Column
            header={t('rtuDetail.instantEvents.columns.eventName', { defaultValue: 'Event Name' })}
            body={(row: InstantSystemEvent) => row.description || row.eventType || row.type || '—'}
            style={{ minWidth: '12rem' }}
          />
          <Column
            header={t('rtuDetail.instantEvents.columns.eventId', { defaultValue: 'Event ID' })}
            body={(row: InstantSystemEvent) => row.eventId ?? row.id ?? '—'}
            style={{ width: '7rem' }}
          />
          <Column field="status" header={t('rtuDetail.instantEvents.columns.status', { defaultValue: 'Status' })} style={{ width: '8rem' }} />
          <Column
            header={t('rtuDetail.instantEvents.columns.category', { defaultValue: 'Category' })}
            body={(row: InstantSystemEvent) => toDisplayValue(row.category)}
            style={{ width: '8rem' }}
          />
          <Column
            header={t('rtuDetail.instantEvents.columns.severity', { defaultValue: 'Severity' })}
            body={(row: InstantSystemEvent) => toDisplayValue(row.severity)}
            style={{ width: '8rem' }}
          />
          <Column
            header={t('rtuDetail.instantEvents.columns.recorded', { defaultValue: 'Recorded' })}
            body={(row: InstantSystemEvent) => formatEventTimestamp(row)}
            style={{ width: '12rem' }}
          />
          <Column
            header={t('rtuDetail.instantEvents.columns.value', { defaultValue: 'Value' })}
            body={(row: InstantSystemEvent) => toDisplayValue(row.value)}
            style={{ width: '7rem' }}
          />
        </DataTable>
      )}
    </Card>
  );
};

const RtuInstantEventHistoryCard = ({ rtuId }: { rtuId: number }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<InstantSystemEvent[]>([]);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await getRtuInstantSystemEventHistory(rtuId, {
          page: Math.floor(first / pageSize),
          size: pageSize,
        });
        if (!active) {
          return;
        }
        if (page.content.length === 0 && page.totalElements > 0 && first > 0) {
          setFirst(0);
          return;
        }
        setRows(page.content);
        setTotalRecords(page.totalElements);
      } catch (loadError: any) {
        if (active) {
          setRows([]);
          setTotalRecords(0);
          setError(
            loadError?.response?.data?.message ||
              loadError?.message ||
              t('rtuDetail.instantEvents.errors.loadFailed', {
                defaultValue: 'Unable to load event history.',
              })
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void run();
    return () => {
      active = false;
    };
  }, [first, pageSize, rtuId, t]);

  useEffect(() => {
    setFirst(0);
    setPageSize(25);
  }, [rtuId]);

  return (
    <Card
      title={t('rtuDetail.sections.instantEventHistory', { defaultValue: 'Event History' })}
      className="info-card compact-card flat-card"
    >
      {error ? <div className="rtu-activity-card__state rtu-activity-card__state--error">{error}</div> : null}
      {loading && rows.length === 0 ? (
        <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
          <SkeletonTable columns={[1.4, 0.8, 0.9, 0.9, 0.9, 1.3, 0.8]} rows={6} showHeader={false} />
        </SkeletonRegion>
      ) : (
        <DataTable
          value={rows}
          loading={loading}
          className="p-datatable-sm"
          showGridlines
          size="small"
          emptyMessage={t('rtuDetail.missing.notReported', { defaultValue: NOT_REPORTED_DEFAULT })}
          tableStyle={{ width: '100%', minWidth: 'max-content' }}
          paginator
          lazy
          first={first}
          rows={pageSize}
          totalRecords={totalRecords}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPage={(event) => {
            setFirst(event.first);
            setPageSize(event.rows);
          }}
        >
          <Column
            header={t('rtuDetail.instantEvents.columns.eventName', { defaultValue: 'Event Name' })}
            body={(row: InstantSystemEvent) => row.description || row.eventType || row.type || '—'}
            style={{ minWidth: '12rem' }}
          />
          <Column
            header={t('rtuDetail.instantEvents.columns.eventId', { defaultValue: 'Event ID' })}
            body={(row: InstantSystemEvent) => row.eventId ?? row.id ?? '—'}
            style={{ width: '7rem' }}
          />
          <Column field="status" header={t('rtuDetail.instantEvents.columns.status', { defaultValue: 'Status' })} style={{ width: '8rem' }} />
          <Column
            header={t('rtuDetail.instantEvents.columns.category', { defaultValue: 'Category' })}
            body={(row: InstantSystemEvent) => toDisplayValue(row.category)}
            style={{ width: '8rem' }}
          />
          <Column
            header={t('rtuDetail.instantEvents.columns.severity', { defaultValue: 'Severity' })}
            body={(row: InstantSystemEvent) => toDisplayValue(row.severity)}
            style={{ width: '8rem' }}
          />
          <Column
            header={t('rtuDetail.instantEvents.columns.recorded', { defaultValue: 'Recorded' })}
            body={(row: InstantSystemEvent) => formatEventTimestamp(row)}
            style={{ width: '12rem' }}
          />
          <Column
            header={t('rtuDetail.instantEvents.columns.value', { defaultValue: 'Value' })}
            body={(row: InstantSystemEvent) => toDisplayValue(row.value)}
            style={{ width: '7rem' }}
          />
        </DataTable>
      )}
    </Card>
  );
};

const RtuModuleHealthCard = ({ modules }: { modules: any[] }) => {
  const { t } = useTranslation();
  const moduleStatusBodyTemplate = useMemo(() => createModuleHealthStatusBodyTemplate(t), [t]);
  const chunkSize = Math.ceil(modules.length / 4) || 1;
  const chunkedData = chunkArray(modules, chunkSize);

  return (
    <Card title={t('rtuDetail.sections.modules')} className="compact-card flat-card modules-card">
      {modules.length === 0 ? (
        <div className="rtu-detail-empty-state">
          {t('rtuDetail.missing.notReported', { defaultValue: NOT_REPORTED_DEFAULT })}
        </div>
      ) : (
        <div className="grid" style={{ margin: 0 }}>
          {chunkedData.map((dataChunk, index) => (
            <div className="col-12 md:col-6" key={index} style={{ padding: '0.25rem' }}>
              <DataTable
                value={dataChunk}
                className="p-datatable-sm"
                showGridlines
                size="small"
                style={{ fontSize: '0.8rem' }}
                tableStyle={{ tableLayout: 'auto', width: '100%' }}
              >
                <Column field="module" header={t('rtuDetail.modules.module')} style={{ whiteSpace: 'nowrap' }} />
                <Column
                  body={moduleStatusBodyTemplate}
                  field="active"
                  header={t('rtuDetail.modules.active')}
                  bodyStyle={{ textAlign: 'center' }}
                  headerStyle={{ textAlign: 'center' }}
                  style={{ width: '1%', whiteSpace: 'nowrap' }}
                />
                <Column
                  body={moduleStatusBodyTemplate}
                  field="license"
                  header={t('rtuDetail.modules.license')}
                  bodyStyle={{ textAlign: 'center' }}
                  headerStyle={{ textAlign: 'center' }}
                  style={{ width: '1%', whiteSpace: 'nowrap' }}
                />
                <Column
                  body={moduleStatusBodyTemplate}
                  field="status"
                  header={t('rtuDetail.modules.status')}
                  bodyStyle={{ textAlign: 'center' }}
                  headerStyle={{ textAlign: 'center' }}
                  style={{ width: '1%', whiteSpace: 'nowrap' }}
                />
              </DataTable>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export const LegacyRtuDetailView = ({ data, modules }: { data: RTUData | null | undefined; modules: any[] }) => {
  const { t } = useTranslation();
  const moduleStatusBodyTemplate = useMemo(() => createModuleHealthStatusBodyTemplate(t), [t]);
  const chunkSize = Math.ceil(modules.length / 4) || 1;
  const chunkedData = chunkArray(modules, chunkSize);

  return (
    <>
      <section className="grid rtu-stable-layout">
        <div className="col-12 md:col-4" style={{ padding: '0.25rem' }}>
          <RTUInfo rtuStaticData={data?.rtuStaticData} rtuHeader={data?.rtuHeader} />
        </div>
        <div className="col-12 md:col-4" style={{ padding: '0.25rem' }}>
          <SystemParameters systemData={data?.systemData} />
        </div>
        <div className="col-12 md:col-4" style={{ padding: '0.25rem' }}>
          <SystemEvents systemEvents={data?.systemEvents} />
        </div>
      </section>

      <section className="grid rtu-stable-layout">
        <div className="col-12" style={{ padding: '0.25rem' }}>
          <Card title={t('rtuDetail.sections.modules')} className="compact-card flat-card modules-card">
            <div className="grid" style={{ margin: 0 }}>
              {chunkedData.map((dataChunk, index) => (
                <div className="col-12 md:col-6 lg:col-3" key={index} style={{ padding: '0.25rem' }}>
                  <DataTable
                    value={dataChunk}
                    className="p-datatable-sm"
                    showGridlines
                    size="small"
                    style={{ fontSize: '0.8rem' }}
                    tableStyle={{ tableLayout: 'auto', width: '100%' }}
                  >
                    <Column field="module" header={t('rtuDetail.modules.module')} style={{ whiteSpace: 'nowrap' }} />
                    <Column
                      body={moduleStatusBodyTemplate}
                      field="active"
                      header={t('rtuDetail.modules.active')}
                      bodyStyle={{ textAlign: 'center' }}
                      headerStyle={{ textAlign: 'center' }}
                      style={{ width: '1%', whiteSpace: 'nowrap' }}
                    />
                    <Column
                      body={moduleStatusBodyTemplate}
                      field="license"
                      header={t('rtuDetail.modules.license')}
                      bodyStyle={{ textAlign: 'center' }}
                      headerStyle={{ textAlign: 'center' }}
                      style={{ width: '1%', whiteSpace: 'nowrap' }}
                    />
                    <Column
                      body={moduleStatusBodyTemplate}
                      field="status"
                      header={t('rtuDetail.modules.status')}
                      bodyStyle={{ textAlign: 'center' }}
                      headerStyle={{ textAlign: 'center' }}
                      style={{ width: '1%', whiteSpace: 'nowrap' }}
                    />
                  </DataTable>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      <section className="grid rtu-stable-layout">
        <div className="col-12" style={{ padding: '0.25rem' }}>
          <RTUDiagnosticsTabbedTree rtuDiagnostics={data?.rtuDiagnostics} />
        </div>
      </section>

      {data?.id ? (
        <section className="grid rtu-stable-layout">
          <div className="col-12 lg:col-6" style={{ padding: '0.25rem' }}>
            <RtuCanonicalFreshnessCard data={data} />
          </div>
          <div className="col-12 lg:col-6" style={{ padding: '0.25rem' }}>
            <RtuStatusHistoryActivityCard rtuId={data.id} />
          </div>
          <div className="col-12 lg:col-6" style={{ padding: '0.25rem' }}>
            <RtuLatestPingCard rtuId={data.id} />
          </div>
          <div className="col-12 lg:col-6" style={{ padding: '0.25rem' }}>
            <RtuReachabilityActivityCard rtuId={data.id} />
          </div>
          <div className="col-12 lg:col-6" style={{ padding: '0.25rem' }}>
            <RtuActionsActivityCard rtuId={data.id} />
          </div>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            <RtuLoginHistoryCard rtuId={data.id} />
          </div>
        </section>
      ) : null}
    </>
  );
};

/** View pills rendered in the breadcrumb trailing slot on `/rtupage/*` only. */
export const RtuViewModePills = ({
  activeView,
  onSwitch,
}: {
  activeView: RtuDetailViewMode;
  onSwitch: (view: RtuDetailViewMode) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="rtu-view-toggle--compact"
      role="group"
      aria-label={t('rtuDetail.viewToggle.ariaGroup', { defaultValue: 'Device detail layout' })}
    >
      <button
        type="button"
        id="rtu-view-pill-basic"
        className={`rtu-view-pill ${activeView === 'basic' ? 'active' : ''}`}
        onClick={() => onSwitch('basic')}
      >
        {t('rtuDetail.viewToggle.basic', { defaultValue: 'Basic' })}
      </button>
      <button
        type="button"
        id="rtu-view-pill-detailed"
        className={`rtu-view-pill ${activeView === 'detailed' ? 'active' : ''}`}
        onClick={() => onSwitch('detailed')}
      >
        {t('rtuDetail.viewToggle.detailed', { defaultValue: 'Detailed' })}
      </button>
      <button
        type="button"
        id="rtu-view-pill-actions"
        className={`rtu-view-pill ${activeView === 'actions' ? 'active' : ''}`}
        onClick={() => onSwitch('actions')}
      >
        {t('rtuDetail.viewToggle.actions', { defaultValue: 'Actions' })}
      </button>
    </div>
  );
};

export const DetailedRtuDetailView = ({ data, modules }: { data: RTUData | null | undefined; modules: any[] }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const latestLogin = useRtuLatestLoginActivity(data?.id);
  const currentInstantEvents = useMemo(
    () => [...getCurrentInstantEvents(data)].sort((left, right) => getEventTimestamp(right) - getEventTimestamp(left)),
    [data]
  );
  const featureTabs = useMemo(
    () => [
      { index: 0, label: t('rtuDetail.tabs.overviewFreshness', { defaultValue: 'Overview and Freshness' }), icon: 'pi pi-th-large' },
      { index: 1, label: t('rtuDetail.tabs.staticConfiguration', { defaultValue: 'Static Configuration' }), icon: 'pi pi-sliders-h' },
      { index: 2, label: t('rtuDetail.tabs.connectivityCommunications', { defaultValue: 'Connectivity and Communications' }), icon: 'pi pi-sitemap' },
      { index: 3, label: t('rtuDetail.tabs.systemMetrics', { defaultValue: 'System Metrics' }), icon: 'pi pi-server' },
      { index: 4, label: t('rtuDetail.tabs.eventsAlarms', { defaultValue: 'Events and Alarms' }), icon: 'pi pi-bell' },
      { index: 5, label: t('rtuDetail.tabs.loginActivity', { defaultValue: 'Login Activity' }), icon: 'pi pi-user' },
      { index: 6, label: t('rtuDetail.tabs.diagnosticsModuleHealth', { defaultValue: 'Diagnostics and Module Health' }), Icon: MonitorHeartOutlined },
    ],
    [t]
  );

  useEffect(() => {
    setActiveTab(0);
  }, [data?.id]);

  return (
    <section className="rtu-detail-tabs flat-surface">
      <div
        className="rtu-detail-tabs__header"
        role="tablist"
        aria-label={t('rtuDetail.tabs.label', { defaultValue: 'RTU detail sections' })}
        style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', padding: '0.45rem 0.15rem 0.75rem' }}
      >
        {featureTabs.map((tab) => (
          <button
            key={tab.index}
            id={`rtu-detail-tab-${tab.index}`}
            role="tab"
            aria-selected={activeTab === tab.index}
            aria-controls={`rtu-detail-tabpanel-${tab.index}`}
            tabIndex={activeTab === tab.index ? 0 : -1}
            type="button"
            onClick={() => setActiveTab(tab.index)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              border: activeTab === tab.index ? '1px solid var(--theme-color)' : '1px solid var(--surface-border)',
              background: activeTab === tab.index ? 'var(--theme-color-light)' : '#fff',
              color: activeTab === tab.index ? 'var(--theme-color)' : 'var(--surface-text)',
              borderRadius: 999,
              padding: '0.35rem 0.7rem',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {'Icon' in tab && tab.Icon ? (
              <tab.Icon sx={{ fontSize: '0.95rem' }} aria-hidden />
            ) : (
              <i className={tab.icon} aria-hidden="true" />
            )}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <TabPanel value={activeTab} index={0}>
        <div className="grid" style={{ margin: 0 }}>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            <div className="rtu-overview-status-row">
              <RtuSessionCard data={data} latestLogin={latestLogin} />
              <RtuCanonicalFreshnessCard data={data} />
              {data?.id ? <RtuLatestPingCard rtuId={data.id} /> : null}
            </div>
          </div>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            <RtuOverviewCards data={data} />
          </div>
          {data?.id ? (
            <div className="col-12" style={{ padding: '0.25rem' }}>
              <RtuReachabilityActivityCard rtuId={data.id} />
            </div>
          ) : null}
        </div>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <div className="grid rtu-static-data-section" style={{ margin: 0 }}>
          <div className="col-12 rtu-static-data-section__info" style={{ padding: '0.25rem' }}>
            <RTUInfo rtuStaticData={data?.rtuStaticData} rtuHeader={data?.rtuHeader} stripCompositeRedundancies />
          </div>
        </div>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <div className="grid" style={{ margin: 0 }}>
          <div className="col-12 lg:col-5" style={{ padding: '0.25rem' }}>
            <RtuConnectivityCard data={data} />
          </div>
          <div className="col-12 lg:col-7" style={{ padding: '0.25rem' }}>
            <RtuCommunicationSummaryCard data={data} />
          </div>
          <div className="col-12 lg:col-12" style={{ padding: '0.25rem' }}>
            <RtuConnectedDevicesCard data={data} />
          </div>
          <div className="col-12 lg:col-12" style={{ padding: '0.25rem' }}>
            <div className="rtu-connectivity-composite-row">
              <div className="rtu-connectivity-composite-row__modem">
                <RtuModemVpnCard data={data} />
              </div>
              <div className="rtu-connectivity-composite-row__virtual">
                <RtuVirtualIpsCard data={data} />
              </div>
              <div className="rtu-connectivity-composite-row__firewall">
                <RtuFirewallCard data={data} />
              </div>
            </div>
          </div>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            <div className="rtu-connectivity-configuration-row">
              <RtuServerPortsCard data={data} />
              <RtuPortForwardCard data={data} />
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <div className="grid" style={{ margin: 0 }}>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            <SystemParameters systemData={data?.systemData} expandContent />
          </div>
        </div>
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <div className="grid" style={{ margin: 0 }}>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            <RtuEventSummaryCard data={data} />
          </div>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            <div className="rtu-events-primary-row">
              <SystemEvents
                systemEvents={data?.systemEvents}
                title={t('rtuDetail.sections.systemEventsSnapshot', { defaultValue: 'System Events Snapshot' })}
              />
              <RtuInstantEventsTable
                title={t('rtuDetail.sections.instantEventCurrent', { defaultValue: 'Current Event Statuses' })}
                rows={currentInstantEvents}
              />
            </div>
          </div>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            {data?.id ? (
              <RtuInstantEventHistoryCard rtuId={data.id} />
            ) : (
              <RtuInstantEventsTable
                title={t('rtuDetail.sections.instantEventHistory', { defaultValue: 'Event History' })}
                rows={[]}
              />
            )}
          </div>
        </div>
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <div className="grid" style={{ margin: 0 }}>
          {data?.id ? (
            <div className="col-12" style={{ padding: '0.25rem' }}>
              <RtuLoginHistoryCard rtuId={data.id} />
            </div>
          ) : null}
        </div>
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <div className="grid" style={{ margin: 0 }}>
          <div className="col-12 lg:col-6" style={{ padding: '0.25rem' }}>
            <RtuDataGenerationCard data={data} />
          </div>
          <div className="col-12 lg:col-6" style={{ padding: '0.25rem' }}>
            <RtuTransferHealthCard data={data} />
          </div>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            <RtuModuleHealthCard modules={modules} />
          </div>
          <div className="col-12" style={{ padding: '0.25rem' }}>
            <RTUDiagnosticsTabbedTree rtuDiagnostics={data?.rtuDiagnostics} />
          </div>
        </div>
      </TabPanel>
    </section>
  );
};

export const EnhancedRtuDetailView = DetailedRtuDetailView;

const RtuDetailPageSkeleton = () => {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })} className="rtu-detail-skeleton">
      <div className="rtu-detail-summary">
        <div className="rtu-detail-summary__status">
          <SkeletonBlock circle height="1.5rem" />
          <SkeletonBlock width="10rem" height="1rem" />
        </div>
        <div className="rtu-detail-summary__updated">
          <SkeletonBlock width="9rem" height="0.9rem" />
        </div>
      </div>
      <div className="rtu-detail-tabs__header" style={{ display: 'flex', gap: '0.4rem', padding: '0.45rem 0.15rem 0.75rem' }}>
        {Array.from({ length: 7 }).map((_, index) => (
          <SkeletonBlock key={index} width="7rem" height="1.6rem" />
        ))}
      </div>
      <div className="grid" style={{ margin: 0 }}>
        {[0, 1, 2].map((index) => (
          <div className="col-12 md:col-4" key={index} style={{ padding: '0.25rem' }}>
            <SkeletonRow count={3} />
          </div>
        ))}
        {[0, 1].map((index) => (
          <div className="col-12 lg:col-6" key={index} style={{ padding: '0.25rem' }}>
            <SkeletonRow count={4} />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
};

const RTUDataDisplay = () => {
  const { rtuId }: any = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { setBreadcrumbTrailingSlot, setRtuBreadcrumbLabel } = useRtuDetailChrome();
  const [searchParams, setSearchParams] = useSearchParams();
  const { RTUsdata, loading, error } = useSelector((state: RootState) => state.rtu);
  const requestedView = searchParams.get('view');
  const activeView: RtuDetailViewMode = resolveRtuDetailViewMode(requestedView);

  useEffect(() => {
    if (rtuId) {
      dispatch(fetchRTUData(rtuId));
    }
  }, [rtuId, dispatch]);

  useEffect(() => {
    if (!requestedView) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('view', 'basic');
      setSearchParams(nextParams, { replace: true });
    }
  }, [requestedView, searchParams, setSearchParams]);

  const modules = useMemo(() => createModuleData(RTUsdata as RTUData | null | undefined), [RTUsdata]);
  const detailActionRows = useMemo(() => {
    if (!RTUsdata) {
      return [];
    }

    return [toRtuActionTarget(RTUsdata)];
  }, [RTUsdata]);

  const switchView = useCallback(
    (view: RtuDetailViewMode) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('view', view);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    setBreadcrumbTrailingSlot(<RtuViewModePills activeView={activeView} onSwitch={switchView} />);
    return () => setBreadcrumbTrailingSlot(null);
  }, [activeView, switchView, setBreadcrumbTrailingSlot]);

  useEffect(() => {
    const name = RTUsdata?.rtuStaticData?.name?.trim();
    setRtuBreadcrumbLabel(name || null);
    return () => setRtuBreadcrumbLabel(null);
  }, [RTUsdata?.rtuStaticData?.name, setRtuBreadcrumbLabel]);

  const detailActions = useMemo(
    () => (
      <RTUActions
        mode="row"
        surface="detail"
        selectedRTUIp={detailActionRows[0]?.publicIPAddress || null}
        selectedRows={detailActionRows}
        syncListStateOnPing={false}
      />
    ),
    [detailActionRows],
  );

  // Bugs 3156/3157/3161: a background Redux/WebSocket refresh for the RTU
  // already on screen must not replace the page with a skeleton/error -
  // that would unmount RTUActions and destroy any open Actions/download
  // dialog and its state. See rtuDetailPageGating for the exact rule.
  if (shouldShowRtuDetailSkeleton({ loading, routeRtuId: rtuId, rtuData: RTUsdata })) {
    return (
      <div className="rtu-page rtu-page--loading">
        <RtuDetailPageSkeleton />
      </div>
    );
  }

  if (shouldShowRtuDetailError({ error, routeRtuId: rtuId, rtuData: RTUsdata })) {
    return (
      <div className="rtu-page">
        <div className="flat-surface" style={{ padding: '1rem', color: '#d32f2f' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="rtu-page">
      {RTUsdata ? (
        <RtuDetailSummaryStrip
          canonicalStatus={RTUsdata.canonicalStatus}
          rtuId={RTUsdata.id}
          name={RTUsdata.rtuStaticData?.name}
          snapshotTimestamp={RTUsdata.timestamp}
          headerUtcTime={RTUsdata.rtuHeader?.utcTime ?? null}
          actions={detailActions}
        />
      ) : null}

      {activeView === 'actions' ? (
        RTUsdata?.id ? (
          <RtuActionsActivityCard rtuId={RTUsdata.id} />
        ) : (
          <div className="flat-surface" style={{ padding: '1rem' }}>
            No RTU selected.
          </div>
        )
      ) : activeView === 'detailed' ? (
        <DetailedRtuDetailView data={RTUsdata as RTUData | null | undefined} modules={modules} />
      ) : (
        <LegacyV2RtuDetailView data={RTUsdata as RTUData | null | undefined} modules={modules} />
      )}
    </div>
  );
};

export default RTUDataDisplay;
